import React, { useState, useEffect } from "react";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import axios from "axios";
import Swal from "sweetalert2";
import { API_BASE } from "../../../ApiUrl";
import { useParams, useNavigate } from "react-router-dom";
import "./AddTenant.css";

import TenantDetailsTab from "./TenantDetailsTab";
import OperationsTab from "./OperationsTab";
import TenantAdminTab from "./TenantAdminTab";
import { TABS } from "./Tenantformoptions";

/* ============================================================
   DEFAULT STATE
   ============================================================ */

const initialTenant = {
  name: "",
  legal_name: "",
  code: "",
  portal_slug: "",
  custom_domain: "",
  status: "TRIAL",
  registration_enabled: true,
  login_enabled: true,
  default_timezone: "",
  default_currency: "USD",
  contact_email: "",
  contact_phone: "",
  logo: "",
  created_by: 1,
  settings: {},
  branding: {},
};

const makeEmptyOperation = () => ({
  _key:
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `op_${Date.now()}_${Math.random().toString(16).slice(2)}`,
  industry: "",
  country_code: "",
  region_name: "",
  is_registration_enabled: true,
  is_active: true,
});

const initialRole = { code: "Admin", name: "Admin", roles_for: "tenant admin" };

const initialUser = {
  email: "",
  mobile_country_code: "+1",
  mobile_number: "",
  password: "",
  role: "Admin",
};

const AddTenant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const [tenant, setTenant] = useState(initialTenant);
  const [operations, setOperations] = useState([makeEmptyOperation()]);
  const [role, setRole] = useState(initialRole);
  const [user, setUser] = useState(initialUser);

  const [errors, setErrors] = useState({});
  const [logoFile, setLogoFile] = useState(null);
  const [existingLogoUrl, setExistingLogoUrl] = useState("");

  useEffect(() => {
    if (id) fetchTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ============================================================
     FETCH (EDIT MODE)
     ============================================================ */
  const fetchTenant = async () => {
    setFetching(true);
    try {
      const res = await axios.get(`${API_BASE}/tenants/${id}`);
      const data = res.data?.data || {};

      const t = data.tenants || data.tenant || {};
      setTenant({
        ...initialTenant,
        ...t,
        settings:
          typeof t.settings === "string"
            ? safeJsonParse(t.settings)
            : t.settings || {},
        branding:
          typeof t.branding === "string"
            ? safeJsonParse(t.branding)
            : t.branding || {},
      });

      // Store existing logo URL
      if (t.logo) {
        setExistingLogoUrl(t.logo);
      }

      const ops = Array.isArray(data.operations) ? data.operations : [];
      setOperations(
        ops.length
          ? ops.map((op) => ({ ...makeEmptyOperation(), ...op }))
          : [makeEmptyOperation()]
      );

      setRole({ ...initialRole, ...(data.role || {}) });
      setUser({ ...initialUser, ...(data.user || {}), password: "" });
    } catch (error) {
      console.error(error);
      Swal.fire("Error", "Failed to load tenant", "error");
    } finally {
      setFetching(false);
    }
  };

  const safeJsonParse = (str) => {
    try {
      return JSON.parse(str);
    } catch {
      return {};
    }
  };

  /* ============================================================
     FIELD HANDLERS
     ============================================================ */
  const handleTenantChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTenant((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    clearError(`tenant.${name}`);
  };

  /* ============================================================
     FILE UPLOAD HANDLER - Stores file for later upload
     ============================================================ */
  const handleFileUpload = async (file) => {
    return new Promise((resolve, reject) => {
      try {
        // Store the file for later upload
        setLogoFile(file);
        
        // Create a preview URL for display
        const previewUrl = URL.createObjectURL(file);
        
        // Update tenant with preview URL using handleTenantChange
        const syntheticEvent = {
          target: {
            name: 'logo',
            value: previewUrl
          }
        };
        handleTenantChange(syntheticEvent);
        
        resolve(previewUrl);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleOperationChange = (index, field, rawValue) => {
    setOperations((prev) =>
      prev.map((op, i) => (i === index ? { ...op, [field]: rawValue } : op))
    );
    clearError(`operations.${index}.${field}`);
  };

  const addOperation = () => setOperations((prev) => [...prev, makeEmptyOperation()]);

  const removeOperation = (index) => {
    setOperations((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleRoleChange = (e) => {
    const { name, value } = e.target;
    setRole((prev) => ({ ...prev, [name]: value }));
    clearError(`role.${name}`);
  };

  const handleUserChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value }));
    clearError(`user.${name}`);
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  /* ============================================================
     VALIDATION
     ============================================================ */
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateStep = (step) => {
    const next = {};

    if (step === 0) {
      if (!tenant.name.trim()) next["tenant.name"] = "Tenant name is required";
      if (!tenant.code.trim()) next["tenant.code"] = "Tenant code is required";
      if (tenant.contact_email && !emailRe.test(tenant.contact_email)) {
        next["tenant.contact_email"] = "Enter a valid email address";
      }
    }

    if (step === 1) {
      if (!operations.length) {
        next["operations._general"] = "Add at least one operation region";
      }
      operations.forEach((op, i) => {
        if (!op.industry) next[`operations.${i}.industry`] = "Industry is required";
        if (!op.country_code) next[`operations.${i}.country_code`] = "Country is required";
        if (!op.region_name.trim()) next[`operations.${i}.region_name`] = "Region name is required";
      });
    }

    if (step === 2) {
      if (!role.code.trim()) next["role.code"] = "Role code is required";
      if (!user.email.trim()) {
        next["user.email"] = "Admin email is required";
      } else if (!emailRe.test(user.email)) {
        next["user.email"] = "Enter a valid email address";
      }
      if (!user.mobile_number.trim()) {
        next["user.mobile_number"] = "Mobile number is required";
      } else if (!/^\d{7,15}$/.test(user.mobile_number.replace(/\s/g, ""))) {
        next["user.mobile_number"] = "Enter a valid mobile number";
      }
      if (!isEdit && !user.password) {
        next["user.password"] = "Password is required";
      } else if (user.password && user.password.length < 8) {
        next["user.password"] = "Password must be at least 8 characters";
      }
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /* ============================================================
     NAVIGATION
     ============================================================ */
  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, 2));
    } else {
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const element = document.querySelector(`[name="${firstErrorKey.split('.').pop()}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
          element.focus();
        }
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const stepClass = (stepNum) => {
    if (stepNum === activeStep) return "at-step at-step--active";
    if (stepNum < activeStep) return "at-step at-step--done";
    return "at-step";
  };

  /* ============================================================
     SUBMIT - USING FORMDATA FOR FILE UPLOAD
     ============================================================ */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const step0Valid = validateStep(0);
    const step1Valid = validateStep(1);
    const step2Valid = validateStep(2);

    if (!step0Valid || !step1Valid || !step2Valid) {
      if (!step0Valid) setActiveStep(0);
      else if (!step1Valid) setActiveStep(1);
      else if (!step2Valid) setActiveStep(2);
      return;
    }

    setLoading(true);
    try {
      const cleanedUser = { ...user };
      if (isEdit && !cleanedUser.password) delete cleanedUser.password;

      // Create FormData for file upload
      const formData = new FormData();

      // Append all tenant fields to FormData
      formData.append('name', tenant.name);
      formData.append('legal_name', tenant.legal_name || '');
      formData.append('code', tenant.code);
      formData.append('portal_slug', tenant.portal_slug || '');
      formData.append('custom_domain', tenant.custom_domain || '');
      formData.append('status', tenant.status);
      formData.append('registration_enabled', tenant.registration_enabled);
      formData.append('login_enabled', tenant.login_enabled);
      formData.append('default_timezone', tenant.default_timezone || '');
      formData.append('default_currency', tenant.default_currency);
      formData.append('contact_email', tenant.contact_email || '');
      formData.append('contact_phone', tenant.contact_phone || '');
      formData.append('created_by', tenant.created_by);
      formData.append('settings', JSON.stringify(tenant.settings || {}));
      formData.append('branding', JSON.stringify(tenant.branding || {}));

      // Append operations as JSON string
      const operationsData = operations.map(({ _key, ...op }) => ({
        ...op,
        industry: Number(op.industry),
      }));
      formData.append('operations', JSON.stringify(operationsData));

      // Append role as JSON string
      formData.append('role', JSON.stringify(role));

      // Append user as JSON string
      formData.append('user', JSON.stringify(cleanedUser));

      // Handle logo - append file if exists
      if (logoFile) {
        // If we have a new logo file, append it
        formData.append('logo', logoFile);
      } else if (isEdit && existingLogoUrl) {
        // If editing and logo exists, keep it (optional)
        // You might want to send a flag or the existing URL
        formData.append('logo', existingLogoUrl);
      }

      // Log FormData contents for debugging
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ' + (pair[1] instanceof File ? pair[1].name : pair[1]));
      }

      // Set headers for FormData - let browser set Content-Type with boundary
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      let response;
      if (isEdit) {
        response = await axios.put(`${API_BASE}/tenants/${id}`, formData, config);
        Swal.fire("Updated!", "Tenant updated successfully", "success");
      } else {
        response = await axios.post(`${API_BASE}/tenants/`, formData, config);
        Swal.fire("Created!", "Tenant added successfully", "success");
      }

      navigate("/superadmin/tenants/tenant-table");
    } catch (error) {
      console.error("Submit Error:", error);
      
      // Handle validation errors from backend
      if (error?.response?.data?.errors) {
        const backendErrors = error.response.data.errors;
        const formattedErrors = {};
        Object.keys(backendErrors).forEach((key) => {
          const errorMessages = backendErrors[key];
          if (Array.isArray(errorMessages)) {
            formattedErrors[`tenant.${key}`] = errorMessages[0];
          } else {
            formattedErrors[`tenant.${key}`] = errorMessages;
          }
        });
        setErrors(formattedErrors);
        setActiveStep(0);
      }
      
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || error?.message || "Something went wrong",
      });
    } finally {
      setLoading(false);
    }
  };

  /* ============================================================
     RENDER
     ============================================================ */
  const stepLabels = ["Tenant", "Operations", "Tenant Admin"];
  const stepIcons = ["🏢", "🌍", "🔐"];

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="page-content">
          <div className="at-wrapper">
            <div className="at-header">
              <div>
                <h2 className="at-header__title">{isEdit ? "Edit Tenant" : "Add Tenant"}</h2>
                <p className="at-header__subtitle">
                  Set up tenant details, its operating regions, and the first admin account.
                </p>
              </div>
            </div>

            {fetching ? (
              <div className="at-card at-loading">Loading tenant…</div>
            ) : (
              <form onSubmit={handleSubmit} className="at-form">
                {/* STEPPER */}
                <div className="at-stepper">
                  {[0, 1, 2].map((step) => (
                    <React.Fragment key={step}>
                      <button
                        type="button"
                        className={stepClass(step)}
                        onClick={() => {
                          if (step < activeStep || step === activeStep) {
                            setActiveStep(step);
                          } else {
                            let canNavigate = true;
                            for (let i = 0; i < step; i++) {
                              if (!validateStep(i)) {
                                canNavigate = false;
                                break;
                              }
                            }
                            if (canNavigate) {
                              setActiveStep(step);
                            }
                          }
                        }}
                      >
                        <span className="at-step__number">{step + 1}</span>
                        <div className="at-step__text">
                          <span className="at-step__label">{stepIcons[step]} {stepLabels[step]}</span>
                        </div>
                      </button>
                      {step < 2 && <div className="at-step__line" />}
                    </React.Fragment>
                  ))}
                </div>

                <div className="at-card">
                  {activeStep === 0 && (
                    <TenantDetailsTab
                      tenant={tenant}
                      errors={errors}
                      onChange={handleTenantChange}
                      onFileUpload={handleFileUpload}
                    />
                  )}

                  {activeStep === 1 && (
                    <OperationsTab
                      operations={operations}
                      errors={errors}
                      onChange={handleOperationChange}
                      onAdd={addOperation}
                      onRemove={removeOperation}
                    />
                  )}

                  {activeStep === 2 && (
                    <TenantAdminTab
                      role={role}
                      user={user}
                      errors={errors}
                      onRoleChange={handleRoleChange}
                      onUserChange={handleUserChange}
                      isEdit={isEdit}
                    />
                  )}
                </div>

                {/* FOOTER ACTIONS */}
                <div className="at-actions">
                  {activeStep === 0 ? (
                    <>
                      <button
                        type="button"
                        className="at-btn at-btn--outline"
                        onClick={() => navigate("/superadmin/tenants/tenant-table")}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="at-btn at-btn--primary"
                        onClick={handleNext}
                        disabled={loading}
                      >
                        Continue →
                      </button>
                    </>
                  ) : activeStep === 1 ? (
                    <>
                      <button
                        type="button"
                        className="at-btn at-btn--outline"
                        onClick={handleBack}
                        disabled={loading}
                      >
                        ← Back
                      </button>
                      <button
                        type="button"
                        className="at-btn at-btn--primary"
                        onClick={handleNext}
                        disabled={loading}
                      >
                        Continue →
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="at-btn at-btn--outline"
                        onClick={handleBack}
                        disabled={loading}
                      >
                        ← Back
                      </button>
                      <button
                        type="submit"
                        className="at-btn at-btn--primary at-btn--submit"
                        disabled={loading}
                      >
                        {loading ? (isEdit ? "Updating…" : "Creating…") : isEdit ? "Update Tenant" : "Create Tenant"}
                      </button>
                    </>
                  )}
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTenant;